import {
  handleThumbnailDrawer,
  pdfStore,
  rotateCw,
  setSearch,
  setZoom,
  usePdfViewerStore,
} from "@/slices/pdfViewer";
import {
  Add,
  ArrowBackIosNew,
  ArrowForwardIos,
  Close,
  Download,
  Menu,
  Remove,
  Rotate90DegreesCcw,
  Search,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const ZOOM_LEVELS = [25, 33, 50, 67, 75, 90, 100, 110, 125, 150, 175, 200];

function InlineSearchBar({ onClose }) {
  const [matchesCount, setMatchesCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const eventBus = pdfStore.getState().eventBus;
  const search = usePdfViewerStore((state) => state.search);

  useEffect(() => {
    if (!eventBus) {
      return;
    }
    const updateMatches = (e) => {
      setCurrentMatch(e.matchesCount.current);
      setMatchesCount(e.matchesCount.total);
    };

    eventBus.on("updatefindmatchescount", updateMatches);
    eventBus.on("updatefindcontrolstate", updateMatches);
    return () => {
      eventBus.off("updatefindmatchescount", updateMatches);
      eventBus.off("updatefindcontrolstate", updateMatches);
    };
  }, [eventBus]);

  const handleSearch = (e) => {
    const value = String(e.target.value);
    setSearch(value);
    setMatchesCount(0);
    setCurrentMatch(0);
    eventBus?.dispatch("find", {
      query: value,
      phraseSearch: false,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
    });
  };
  const onNavigation = (direction) => {
    eventBus?.dispatch("find", {
      query: search,
      type: "again",
      findPrevious: direction === "prev",
      phraseSearch: false,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
    });
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onNavigation("next");
    }
  };
  const handleCloseAndClear = () => {
    eventBus?.dispatch("find", {
      query: "",
      phraseSearch: false,
      caseSensitive: false,
      entireWord: false,
      highlightAll: false,
    });
    setMatchesCount(0);
    setCurrentMatch(0);
    setSearch("");
    onClose();
  };

  return (
    <TextField
      onChange={handleSearch}
      onKeyDown={handleKeyDown}
      value={search}
      placeholder="Search in document"
      size="small"
      className="flex-1"
      autoFocus
      aria-label="Search in document"
      InputProps={{
        className: "h-7 min-w-[185px] text-sm",
        endAdornment: (
          <Box className="flex items-center">
            <Typography className="px-1 text-xs" noWrap>
              {`${currentMatch} / ${matchesCount}`}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onNavigation("prev")}
              disabled={matchesCount === 0}
              className="rounded-md"
            >
              <ArrowBackIosNew fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onNavigation("next")}
              disabled={matchesCount === 0}
              className="rounded-md"
            >
              <ArrowForwardIos fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCloseAndClear}
              className="rounded-md"
            >
              <Close fontSize="inherit" />
            </IconButton>
          </Box>
        ),
      }}
    />
  );
}

/**
 * Toolbar component for managing PDF viewer controls and document interactions.
 */
export default function Toolbar({ fileUrl, fileName }) {
  const pdfViewer = pdfStore.getState().pdfViewer;
  const page = usePdfViewerStore((state) => state.page);
  const zoom = usePdfViewerStore((state) => state.zoom);
  const totalPages = usePdfViewerStore((state) => state.totalPages);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const rotate = usePdfViewerStore((state) => state.rotate);

  const handleCloseSearchBar = () => {
    setSearchBarOpen(false);
  };

  // Zoom handlers
  const handleZoomIn = () => {
    if (pdfViewer) {
      let newZoom;
      if (isNaN(zoom)) {
        newZoom = 110;
      } else {
        const currentIndex = ZOOM_LEVELS.findIndex((level) => level > zoom);
        newZoom =
          ZOOM_LEVELS[currentIndex] ?? ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
      }
      setZoom(newZoom);
      pdfViewer.currentScale = newZoom / 100;
    }
  };

  const handleZoomOut = () => {
    if (pdfViewer) {
      let newZoom;
      if (isNaN(zoom)) {
        newZoom = 90;
      } else {
        const currentIndex = ZOOM_LEVELS.findLastIndex((level) => level < zoom);
        newZoom = ZOOM_LEVELS[currentIndex] ?? ZOOM_LEVELS[0];
      }
      setZoom(newZoom);
      pdfViewer.currentScale = newZoom / 100;
    }
  };

  const handleZoomChange = (event) => {
    if (pdfViewer) {
      setZoom(event.target.value);
      if (isNaN(event.target.value) === false) {
        pdfViewer.currentScale = event.target.value / 100;
      } else {
        pdfViewer.currentScaleValue = event.target.value;
      }
    }
  };

  const handleRotate = () => {
    if (pdfViewer) {
      rotateCw();
      pdfViewer.pagesRotation = rotate === 270 ? 0 : rotate + 90;
    }
  };

  const handleDownload = () => {
    if (pdfViewer) {
      pdfViewer.downloadManager.download(undefined, fileUrl, fileName);
    }
  };

  const handleSearchClick = () => {
    setSearchBarOpen(true);
  };

  return (
    <Box className="@container/toolbar z-[1] flex items-center justify-between gap-1 p-2 shadow-sm">
      <Box className="flex min-w-0 items-center gap-2">
        <Tooltip title="Show Thumbnails">
          <IconButton
            className="@max-3xs:hidden"
            onClick={handleThumbnailDrawer}
          >
            <Menu />
          </IconButton>
        </Tooltip>
        <Typography
          title={fileName}
          className="dark:text-dark-blue-400 truncate"
        >
          {fileName}
        </Typography>
      </Box>

      {/* Page Navigation */}
      <Box className="flex items-center space-x-2">
        <Typography className="dark:border-blue-100 dark:text-blue-400 rounded-md blue-50 px-3 dark:border">
          {page}
        </Typography>
        <Typography className="text-dark-blue-200"> / </Typography>
        <Typography className="text-dark-blue-200">
          {totalPages || "?"}
        </Typography>
      </Box>

      {/* Zoom Controls */}
      <Box className="flex items-center gap-2 @max-sm:hidden">
        <Tooltip title="Zoom Out">
          <IconButton size="small" onClick={handleZoomOut}>
            <Remove className="text-[length:inherit]" />
          </IconButton>
        </Tooltip>
        <Select
          size="small"
          className="dark:text-dark-blue-400 h-7 max-w-36"
          value={zoom}
          onChange={handleZoomChange}
        >
          <MenuItem value="auto">Auto</MenuItem>
          <MenuItem value="page-actual">Actual Size</MenuItem>
          <MenuItem value="page-fit">Page Fit</MenuItem>
          <MenuItem value="page-width">Page Width</MenuItem>
          {ZOOM_LEVELS.map((zoomLevel) => (
            <MenuItem key={zoomLevel} value={zoomLevel.toString()}>
              {zoomLevel}%
            </MenuItem>
          ))}
        </Select>
        <Tooltip title="Zoom In">
          <IconButton size="small" onClick={handleZoomIn}>
            <Add className="dark:text-dark-blue-400 text-[length:inherit]" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className="flex items-center gap-2">
        {searchBarOpen ? (
          <InlineSearchBar onClose={handleCloseSearchBar} />
        ) : (
          <>
            <Tooltip key="rotate" title="Rotate Document">
              <IconButton
                className="hidden @min-lg/toolbar:flex"
                size="small"
                onClick={handleRotate}
              >
                <Rotate90DegreesCcw className="dark:text-blue-400 text-[length:inherit]" />
              </IconButton>
            </Tooltip>
            <Tooltip key="search" title="Search">
              <IconButton
                className="hidden @min-lg/toolbar:flex"
                size="small"
                onClick={handleSearchClick}
              >
                <Search className="dark:text-blue-400 text-[length:inherit]" />
              </IconButton>
            </Tooltip>
            <Tooltip key="download" title="Download Document">
              <IconButton
                className="hidden @min-lg/toolbar:flex"
                size="small"
                onClick={handleDownload}
              >
                <Download className="dark:text-blue-400 text-[length:inherit]" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
